import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'

import { DUE_DATE_METHOD, theme } from '@hhgtech/hhg-components'
import { Button, Select, Text } from '@hhgtech/hhg-components/mantine'
import { Box, Image, Stack, rem } from '@mantine/core'
import { useForm } from '@mantine/form'
import dayjs from 'dayjs'
import { useIntl } from 'react-intl'

import AdditionalQuestionGroup, {
  HandlerProps as AdditionalQuestionHandlerProps,
} from 'components/atoms/healthTools/form/additionalQuestionGroup'
import { AgeInput } from 'components/atoms/healthTools/form/ageInput'
import { CheckYourSelfSwitching } from 'components/atoms/healthTools/form/checkYourSelfSwitching'
import { AdBlock } from 'components/molecules/adBlock'
import { BmiFormHelpers } from 'components/molecules/bmiForm'
import { StyledFormControl } from 'components/organisms/babyGrowthForm/index.styled'
import { DueDateInfoType } from 'components/pages/healthTools/due-date'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { DISPLAY_DATE_FORMAT, assetImagePath } from 'interfaces/constants'
import { AdditionalQuestionFieldType } from 'interfaces/types'
import { AppContext } from 'state/appContext'
import { fastdom } from 'utils/fastdom'

import DueDateFormConception, {
  HandlerProps as DueDateFormConceptionHandlerProps,
} from './dueDateFormConception'
import DueDateFormIUI from './dueDateFormIUI'
import DueDateFormIVF, {
  HandlerProps as DueDateFormIVFHandlerProps,
} from './dueDateFormIVF'
import DueDateFormLastPeriod, {
  HandlerProps as DueDateFormLastPeriodHandlerProps,
} from './dueDateFormLastPeriod'
import DueDateFormUltrasound from './dueDateFormUltrasound'
import {
  DueDateForm,
  // StyledCategoryCard,
  // StyledMethodCardWrapper,
  StyledMethodContainer,
  // StyledMethodSwitcherContainer,
  StyledScrollLinkContainer,
} from './index.styled'
import {
  DUE_DATE_CONFIG,
  DueDateCalculatorType,
  DueDateFormValues,
  STORE_KEY,
  useDueDateStore,
} from './utils'

export { DueDateCalculatorType }

export type Props = {
  onSubmitDueDate?: (info: DueDateInfoType, redirectLink?: string) => void
  isEmbed?: boolean
  dueDateInfo?: [
    {
      title: string
      description: string
    },
  ]
  adUnitPath?: string
  controlledSubmit?: boolean
  additionalQuestions?: AdditionalQuestionFieldType[]
}

type ItemProps = {
  value: string
  imageSrc: string
  label: string
}
const SelectMethodItem = forwardRef<HTMLDivElement, ItemProps>(
  ({ value, label, imageSrc, ...rest }: ItemProps, ref) => {
    return (
      <Box
        ref={ref}
        sx={{
          gap: rem(8),
          display: 'flex',
          padding: '5px 6px',
          alignItems: 'center',
          '&[data-selected]': {
            backgroundColor: theme.colors.gray100,
          },
          '&[data-selected]:hover': {
            backgroundColor: theme.colors.gray100,
          },
          '&[data-hovered]': {
            backgroundColor: theme.colors.gray100,
          },
        }}
        className=""
        {...rest}
      >
        <img
          src={imageSrc}
          style={{
            width: '20px',
            height: '20px',
            objectFit: 'contain',
            maxWidth: '100%',
          }}
          alt={label}
          loading="lazy"
        />
        <Text size="p2">{label}</Text>
      </Box>
    )
  },
)

const DueDateCalculator = ({
  additionalQuestions = [],
  onSubmitDueDate,
  isEmbed = false,
  controlledSubmit,
  adUnitPath,
}: Props) => {
  const additionQuestionRef = useRef<AdditionalQuestionHandlerProps>(null)
  const { formatMessage: f } = useIntl()
  const { router, locale } = useRouterExtend()
  const {
    state: { userInfo },
  } = useContext(AppContext)
  const ref = useRef<
    | DueDateFormConceptionHandlerProps
    | DueDateFormIVFHandlerProps
    | DueDateFormLastPeriodHandlerProps
  >(null)
  const { calculatorType, setCalculatorType, isCalculating } = useDueDateStore()
  const form = useForm<DueDateFormValues>({
    initialValues: {
      birthday: undefined,
      isFirstPregnant: true,
      isMyself: true,
      ultrasoundCRL: undefined,
      ultrasoundMSD: undefined,
    },
    validate(values) {
      const commonErrors = !values.birthday
        ? {
            birthday: f({ id: 'validation.error.requiredField' }),
          }
        : {}

      if (calculatorType === DueDateCalculatorType.ultrasound) {
        const ultrasoundCRL = Number(values.ultrasoundCRL)
        const ultrasoundMSD = Number(values.ultrasoundMSD)

        if (!ultrasoundCRL && !ultrasoundMSD) {
          return {
            ultrasoundCRL: f({
              id: 'dueDatePage.validation.record_not_valid',
              defaultMessage: "Your recorded isn't valid!",
            }),
            ...commonErrors,
          }
        }

        const isCRLValid = ultrasoundCRL >= 5 && ultrasoundCRL <= 101 // Range 5 -> 101
        const isMSDValid = ultrasoundMSD > 0 && ultrasoundMSD <= 13 // Range 0 -> 13

        if (isCRLValid || isMSDValid) return { ...commonErrors }

        if (ultrasoundCRL && !isCRLValid) {
          return {
            ultrasoundCRL: f({
              id: 'dueDatePage.validation.record_not_valid',
              defaultMessage: "Your recorded isn't valid!",
            }),
            ...commonErrors,
          }
        }
        if (ultrasoundMSD && !isMSDValid) {
          return {
            ultrasoundMSD: f({
              id: 'dueDatePage.validation.crl',
              defaultMessage:
                'The MSD value is invalid. Please enter the CRL metric to continue.',
            }),
            ...commonErrors,
          }
        }
      }
      return { ...commonErrors }
    },
  })

  const calculatingMethods = useMemo(() => {
    return DUE_DATE_METHOD[locale].map((method) => {
      const config = DUE_DATE_CONFIG[method]
      return { ...config, label: f({ id: config.label }) }
    })
  }, [locale])

  const scrollIntoExplaination = useCallback(() => {
    fastdom.measure(() => {
      const info = document.querySelector('#calculator-info')
      if (info) {
        const top = info.getBoundingClientRect().top + window.scrollY - 80
        window.scrollTo({
          top,
          behavior: 'smooth',
        })
      }
    })
  }, [])

  const onBeforeSubmit = async (
    info: DueDateInfoType,
    redirectLink?: string,
  ) => {
    const birthday = form.getInputProps('birthday').value
    const data = {
      ...info,
      ...(birthday && {
        birthday: dayjs(birthday).format(DISPLAY_DATE_FORMAT),
      }),
      isMyself: Number(Boolean(form.getInputProps('isMyself').value)),
      isFirstPregnant: Number(
        Boolean(form.getInputProps('isFirstPregnant').value),
      ),
      ...(form.getInputProps('additionalQuestions').value?.length > 0 && {
        additionalQuestions: form.getInputProps('additionalQuestions').value,
      }),
    }

    localStorage.setItem(
      STORE_KEY,
      JSON.stringify({
        ...data,
        isSubmitted: Boolean(userInfo?.id),
        source_url: window.location.href,
      }),
    )

    onSubmitDueDate?.(data, redirectLink)
  }

  useEffect(() => {
    if (userInfo?.id) {
      form.setFieldValue('isMyself', true)
      if (userInfo?.birthday) {
        form.setFieldValue('birthday', dayjs(userInfo.birthday).toDate())
      }
    }
  }, [userInfo?.id])

  const birthday = form.getInputProps('birthday').value
  const isMyself = form.getInputProps('isMyself').value

  useEffect(() => {
    if (userInfo === null) {
      BmiFormHelpers.syncParamsToUseOnSSOForm(
        {
          gender: isMyself ? 'female' : '',
          birthday: isMyself ? birthday : undefined,
        },
        router,
      )
    }
  }, [birthday, userInfo, isMyself])

  const selectedMethod = useMemo(() => {
    return calculatingMethods.find((o) => o.value === String(calculatorType))
  }, [calculatingMethods, calculatorType])

  return (
    <Box pb={24}>
      <DueDateForm onSubmit={form.onSubmit(() => ref.current?.submitForm())}>
        <Stack spacing={rem(12)}>
          <StyledFormControl>
            <AgeInput {...form.getInputProps('birthday')} />
          </StyledFormControl>
          <StyledFormControl>
            <CheckYourSelfSwitching
              checked={form.getInputProps('isMyself').value}
              {...form.getInputProps('isMyself')}
              onChange={(e) =>
                form.setFieldValue('isMyself', e.currentTarget.checked)
              }
            />
            <CheckYourSelfSwitching
              {...form.getInputProps('isFirstPregnant')}
              defaultChecked
              label={f({
                id: 'tools.isFirstPregnant',
                defaultMessage: 'Is this the first pregnant?',
              })}
            />
          </StyledFormControl>
          <StyledMethodContainer>
            <Box className="method-label" mb={4}>
              <Text as="label" size="s4">
                {f({ id: 'dueDatePage.calculationMethod' })}
              </Text>
              <Image
                width={18}
                height={18}
                src={`${assetImagePath}/info-icon.svg`}
                alt=""
                onClick={scrollIntoExplaination}
              />
            </Box>

            <Select
              value={String(calculatorType)}
              placeholder={f({ id: 'dueDatePage.calculationMethod' })}
              data={calculatingMethods}
              itemComponent={SelectMethodItem}
              onChange={(newType: string) => setCalculatorType(Number(newType))}
              icon={
                <img
                  src={selectedMethod?.imageSrc}
                  style={{
                    width: '20px',
                    height: '20px',
                    objectFit: 'contain',
                    maxWidth: '100%',
                  }}
                  alt={selectedMethod?.imageSrc}
                  loading="lazy"
                />
              }
              styles={{
                input: {
                  '&[data-with-icon]': {
                    paddingLeft: '2.3rem',
                  },
                },
              }}
            />
            {!isEmbed && (
              <StyledScrollLinkContainer></StyledScrollLinkContainer>
            )}
          </StyledMethodContainer>
          <Box>
            {calculatorType === DueDateCalculatorType.lastPeriod ? (
              <DueDateFormLastPeriod
                ref={ref}
                isEmbed={isEmbed}
                controlledSubmit={controlledSubmit}
                onSubmit={onBeforeSubmit}
              />
            ) : null}
            {calculatorType === DueDateCalculatorType.IUI ? (
              <DueDateFormIUI
                ref={ref}
                isEmbed={isEmbed}
                controlledSubmit={controlledSubmit}
                onSubmit={onBeforeSubmit}
              />
            ) : null}
            {calculatorType === DueDateCalculatorType.ultrasound ? (
              <DueDateFormUltrasound
                ref={ref}
                isEmbed={isEmbed}
                controlledSubmit={controlledSubmit}
                onSubmit={onBeforeSubmit}
                form={form}
                scrollIntoExplaination={scrollIntoExplaination}
              />
            ) : null}
            {calculatorType === DueDateCalculatorType.conception ? (
              <DueDateFormConception
                ref={ref}
                isEmbed={isEmbed}
                controlledSubmit={controlledSubmit}
                onSubmit={onBeforeSubmit}
              />
            ) : null}
            {calculatorType === DueDateCalculatorType.IVF ? (
              <DueDateFormIVF
                ref={ref}
                isEmbed={isEmbed}
                controlledSubmit={controlledSubmit}
                onSubmit={onBeforeSubmit}
              />
            ) : null}
            <AdditionalQuestionGroup
              ref={additionQuestionRef}
              additionalQuestions={additionalQuestions}
              onChange={(v) => form.setFieldValue('additionalQuestions', v)}
              className="mb-4"
            />
          </Box>
          <Button
            type="submit"
            className="due-date-calculate-button"
            size="lg"
            color="primary"
            dataEventCategory="Health Tool"
            dataEventAction="Due Date"
            fullWidth
            loading={isCalculating}
          >
            {f({
              id: isCalculating
                ? 'dueDatePage.calculating'
                : 'dueDatePage.lastPeriod.calculate',
            })}
          </Button>
        </Stack>
      </DueDateForm>
      {adUnitPath && (
        <AdBlock
          adUnitPath={adUnitPath}
          size={[300, 250]}
          divId={'div-gpt-ad-topbanner-mobile'}
          pos={['TopBanner']}
        />
      )}
    </Box>
  )
}

export { DueDateCalculator }
